/**
 * CoworkerWatcher — global browser-coworker notifications for the unified
 * layout (docs/BROWSER_COWORKER_PLAN.md M2). Mounted once in the app
 * layout: follows the user's ACTIVE task sessions' event streams and
 * surfaces an actionable toast and a browser-tab title when the agent
 * needs the user — `browser_login_requested` (blocked on a login handoff)
 * or `browser_confirmation_requested` (approve/deny gate) — even when the
 * user is on a different screen. Toasts deep-link to the thread's
 * conversation (/c/:conversationId) where the banner/viewer live.
 *
 * Dormant unless there is at least one queued/running task; SSE where
 * available, gentle polling otherwise. Fires at most one toast per event
 * seq, and stays quiet while the user is already viewing that thread.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { usePathname } from 'expo-router';
import { useAuthStore, useToastStore, useCoworkerStore } from '@/store';
import { coworkerApi } from '@/lib/api';
import { slimWorkEvent, isStaleWorkSseFrame, workPendingInteractionFromEvents } from '@/constants/work';
import { sidebarStatusEvents } from '@/lib/sidebarChatStatus';
import { useMacAccessRequests } from '@/lib/macAccessRequests';
import MacWorkNotificationsHost from './MacWorkNotificationsHost';
import { workLaunchWait } from '@/lib/workLaunchWait';
import MobileWorkActivityHost from './MobileWorkActivityHost';
import useLiveWorkCredits from '@/hooks/useLiveWorkCredits';
import {
  clearWorkTabAttention,
  initWorkTabAttention,
  signalWorkTabAttention,
  syncWorkTabAttentionTitle,
} from '@/lib/workTabAttention';

const ACTIVE_STATUSES = ['queued', 'running'];
// Refresh the task list occasionally so a thread launched elsewhere (or a
// schedule) gets picked up without waiting for settle events.
const TASK_LIST_REFRESH_MS = 60 * 1000;
const POLL_INTERVAL_MS = 6000;
const WATCHER_EVENT_CAP = 80;

// Pending interactive browser state from an event log (same derivation as
// useCoworkerChat / the live panel): the last request without a resolution.
function derivePendingInteraction(events) {
  return workPendingInteractionFromEvents(events);
}

export default function CoworkerWatcher() {
  const pathname = usePathname();
  const isAuthenticated = !!useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.id);
  const sessionGeneration = useAuthStore((s) => s._sessionGeneration);
  const { addToast } = useToastStore((s) => s.actions);

  const lastSeqRef = useRef({});        // taskId -> highest seen seq
  const notifiedRef = useRef({});       // `${taskId}:${seq}` -> true
  const notifiedKeysRef = useRef([]);
  const followingByTask = useCoworkerStore((s) => s.followingByTask);
  // Active tasks as state: the follow effect re-runs when the periodic
  // refresh discovers new active threads (launched elsewhere / schedules).
  const [activeTasks, setActiveTasks] = useState([]);
  const hasKnownActiveWork = useCoworkerStore(s => Object.values(s.tasks).some(t => ACTIVE_STATUSES.includes(t.status)));
  useLiveWorkCredits(hasKnownActiveWork || activeTasks.length > 0, userId, sessionGeneration, isAuthenticated);
  const macRequests = useMacAccessRequests(state => state.requests);
  const macNotifiedRef = useRef(new Set());

  // Which conversation the user is currently viewing (stay quiet there —
  // the in-thread banner + live panel already show it).
  const viewingConversationId = useMemo(() => {
    const match = pathname?.match(/^\/c\/([a-f0-9]+)/i);
    return match ? match[1] : null;
  }, [pathname]);

  useEffect(() => initWorkTabAttention(), []);

  // A lost create reply or reload must resolve the saved submission, never
  // generate a new launch identity. Recovery adds the chat without navigating
  // away from whatever the user is doing now.
  useEffect(() => {
    if (Platform.OS !== 'web' || !isAuthenticated || !userId) return;
    let stopped = false, busy = false;
    const recover = async () => {
      if (stopped || busy) return;
      busy = true;
      try {
        const recovered = await useCoworkerStore.getState().recoverTaskSubmissions();
        if (stopped) return;
        for (const task of recovered) addToast('Your Work request is saved.', 'info', [
          { title: 'Open', icon: 'arrow-right', href: `/c/${task.conversationId}` },
        ], { category: 'coworker' });
      } catch (_) { /* Storage or auth failures preserve the saved request. */ }
      finally { busy = false; }
    };
    void recover();
    const timer = setInterval(recover, 10000);
    return () => { stopped = true; clearInterval(timer); };
  }, [isAuthenticated, userId, sessionGeneration, addToast]);

  useEffect(() => {
    lastSeqRef.current = {};
    notifiedRef.current = {};
    notifiedKeysRef.current = [];
    macNotifiedRef.current.clear();
    setActiveTasks([]);
  }, [userId, sessionGeneration]);

  // Native permission cards wait inside a live tool call, so they never emit
  // the server question events watched below. Surface those local gates too.
  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    const requests = macRequests.filter(request => request.userId === String(userId) && request.isCurrent?.() !== false);
    const pendingIds = new Set(requests.map(request => request.id));
    macNotifiedRef.current = new Set([...macNotifiedRef.current].filter(id => pendingIds.has(id)));
    for (const request of requests) {
      if ((request.busy && !request.waitingForSystem) || macNotifiedRef.current.has(request.id)) continue;
      const task = useCoworkerStore.getState().tasks[request.taskId] || activeTasks.find(row => row.id === request.taskId);
      const conversation = task?.conversation || task?.conversationId;
      if (!conversation || ['stopped', 'failed'].includes(task?.status)) continue;
      macNotifiedRef.current.add(request.id);
      if (String(conversation) === viewingConversationId) continue;
      addToast('Work needs Mac access', 'info', [{ title: 'Open', icon: 'arrow-right', href: `/c/${conversation}` }], { category: 'coworker' });
      signalWorkTabAttention({ kind: 'approval', threadTitle: task.title, taskId: request.taskId, conversationId: conversation });
    }
  }, [macRequests, isAuthenticated, userId, sessionGeneration, viewingConversationId, activeTasks, addToast]);

  useEffect(() => {
    if (viewingConversationId) {
      clearWorkTabAttention({ conversationId: viewingConversationId });
      // A different thread's "● Done · …" must not stay on this URL.
      syncWorkTabAttentionTitle(pathname);
      return;
    }
    // Leaving /c/:id (home, files, …) must drop "● Done · Thread" so the
    // dashboard tab is not stuck on the last Work title.
    if (!pathname || pathname === '/app' || pathname.startsWith('/app')) {
      clearWorkTabAttention();
    }
  }, [viewingConversationId, pathname]);

  const maybeNotify = useCallback((task, events) => {
    if (!task) return;
    const viewingThisThread = !!(
      task.conversation && String(task.conversation) === viewingConversationId
    );
    const { loginRequest, confirmation, question } = derivePendingInteraction(events);
    const openAction = task.conversation
      ? [{ title: 'Open', icon: 'arrow-right', href: `/c/${task.conversation}` }]
      : [];
    const markNotified = (key) => {
      if (notifiedRef.current[key]) return false;
      notifiedRef.current[key] = true;
      notifiedKeysRef.current.push(key);
      if (notifiedKeysRef.current.length > 200) {
        const dropped = notifiedKeysRef.current.shift();
        if (dropped) delete notifiedRef.current[dropped];
      }
      return true;
    };

    if (loginRequest) {
      const key = `${task.id}:${loginRequest.seq}`;
      if (markNotified(key)) {
        if (!viewingThisThread) {
          addToast('Work needs you to sign in', 'info', openAction, { category: 'coworker' });
        }
        signalWorkTabAttention({
          kind: 'login',
          threadTitle: task.title,
          taskId: task.id,
          conversationId: task.conversation,
        });
      }
    }
    if (confirmation) {
      const key = `${task.id}:${confirmation.seq}`;
      if (markNotified(key)) {
        if (!viewingThisThread) {
          addToast('Work needs your approval', 'info', openAction, { category: 'coworker' });
        }
        signalWorkTabAttention({
          kind: 'approval',
          threadTitle: task.title,
          taskId: task.id,
          conversationId: task.conversation,
        });
      }
    }
    if (question) {
      const key = `${task.id}:${question.seq}`;
      if (markNotified(key)) {
        if (!viewingThisThread) {
          addToast(
            question.kind === 'credits'
              ? 'Work needs more credits'
              : (question.kind === 'plan'
                ? 'Work has a plan ready for you'
                : ((question.kind === 'cost' || question.kind === 'memory') ? 'Work needs your approval' : 'Work has a question for you')),
            'info',
            openAction,
            { category: 'coworker' },
          );
        }
        signalWorkTabAttention({
          kind: question.kind === 'credits' ? 'credits' : ((question.kind === 'cost' || question.kind === 'memory' || question.kind === 'plan') ? 'approval' : 'question'),
          threadTitle: task.title,
          taskId: task.id,
          conversationId: task.conversation,
        });
      }
    }
  }, [addToast, viewingConversationId]);

  // Discover active tasks; refresh periodically.
  useEffect(() => {
    if (!isAuthenticated) return;
    let stopped = false;

    const refresh = async () => {
      const tasks = await useCoworkerStore.getState().fetchTasks();
      if (stopped || !Array.isArray(tasks)) return;
      const next = tasks
        .filter((t) => ACTIVE_STATUSES.includes(t.status))
        .map((t) => ({
          id: t.id,
          title: t.title || t.name || null,
          conversation: t.conversation || t.conversationId || null,
          status: t.status,
          launchQueue: t.launchQueue,
        }));
      setActiveTasks((prev) => {
        const same = prev.length === next.length
          && prev.every((t, i) => (
            t.id === next[i].id
            && t.conversation === next[i].conversation
            && t.title === next[i].title
          ));
        return same ? prev : next;
      });
    };

    refresh();
    const interval = setInterval(refresh, TASK_LIST_REFRESH_MS);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, userId, sessionGeneration]);

  // The notifier closes over the current route. Reading it through a ref
  // means navigating between chats does not tear down and re-open every
  // active task's EventSource (each reopen replays the whole log).
  const maybeNotifyRef = useRef(maybeNotify);
  useEffect(() => {
    maybeNotifyRef.current = maybeNotify;
  }, [maybeNotify]);

  // In-thread useCoworkerChat already owns followed tasks — a second
  // EventSource would replay the full log and double-parse every frame.
  // Keyed by id list so a follow-count change on a task we were already
  // skipping (or already following) does not rebuild the others.
  const tasksToFollow = useMemo(
    () => activeTasks.filter((task) => !followingByTask?.[task.id]),
    [activeTasks, followingByTask],
  );
  const followKey = useMemo(
    () => tasksToFollow.map((task) => `${task.id}:${task.conversation || ''}:${task.title || ''}`).join('|'),
    [tasksToFollow],
  );
  const tasksToFollowRef = useRef(tasksToFollow);
  tasksToFollowRef.current = tasksToFollow;

  // Follow each active task's events (SSE or poll) and notify.
  useEffect(() => {
    if (!isAuthenticated) return;
    let stopped = false;
    const sources = [];

    const followTask = async (task) => {
      if (Platform.OS === 'web' && workLaunchWait(task)) {
        let timer;
        sources.push(() => clearTimeout(timer));
        const poll = async () => {
          if (stopped) return;
          const fresh = await coworkerApi.fetchTask(task.id).catch(() => null);
          const auth = useAuthStore.getState();
          if (stopped || auth.user?.id !== userId || auth._sessionGeneration !== sessionGeneration) return;
          const existing = useCoworkerStore.getState().tasks[task.id];
          if (existing && !workLaunchWait(existing)) {
            if (ACTIVE_STATUSES.includes(existing.status)) await followTask({ ...task, ...existing });
            return;
          }
          if (fresh) useCoworkerStore.getState().addTask(fresh);
          if (fresh && !workLaunchWait(fresh)) {
            if (ACTIVE_STATUSES.includes(fresh.status)) await followTask({ ...task, ...fresh });
            return;
          }
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        };
        void poll();
        return;
      }
      const events = [];
      const ingest = (incoming) => {
        if (stopped) return;
        const auth = useAuthStore.getState();
        if (auth.user?.id !== userId || auth._sessionGeneration !== sessionGeneration) return;
        // Share interaction/lifecycle events with sidebar rows while another
        // chat is open. Do not keep background token/tool traffic in UI state.
        const statusEvents = sidebarStatusEvents(incoming);
        useCoworkerStore.getState().ingestTaskEvents(task.id, statusEvents);
        for (const ev of incoming || []) {
          if (!ev || !Number.isFinite(ev.seq)) continue;
          if (ev.seq <= (lastSeqRef.current[task.id] || 0)) continue;
          lastSeqRef.current[task.id] = ev.seq;
          events.push(slimWorkEvent(ev));
          if (events.length > WATCHER_EVENT_CAP) events.splice(0, events.length - WATCHER_EVENT_CAP);
        }
        maybeNotifyRef.current(task, events);
      };

      // Polling fallback: incremental by seq. Declared first so the SSE
      // error handler can reach it when EventSource closes fatally.
      let pollTimer = null;
      let polling = false;
      const startPolling = () => {
        if (polling) return;
        polling = true;
        sources.push(() => { if (pollTimer) clearTimeout(pollTimer); });
        const tick = async () => {
          if (stopped) return;
          const since = lastSeqRef.current[task.id] || 0;
          const data = await coworkerApi.fetchTaskEvents(task.id, since).catch(() => null);
          if (stopped) return;
          if (data) ingest(data.events);
          // Keep polling while the effect owns this task; staleness is
          // bounded by the periodic task-list refresh rebuilding the set.
          pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
        };
        tick();
      };

      if (typeof EventSource !== 'undefined') {
        let source = null;
        try {
          const url = await coworkerApi.getTaskEventsSseUrl(task.id, lastSeqRef.current[task.id] || 0);
          if (stopped) return;
          source = new EventSource(url);
          source.onmessage = (msg) => {
            try {
              if (isStaleWorkSseFrame(msg.data, lastSeqRef.current[task.id] || 0)) return;
              ingest([JSON.parse(msg.data)]);
            } catch { /* skip */ }
          };
          source.addEventListener('eot', () => source?.close());
          source.onerror = () => {
            // Fatal endpoint failure → poll instead of going blind.
            if (stopped) { source?.close(); return; }
            if (source && source.readyState === EventSource.CLOSED) {
              source.close();
              startPolling();
            }
          };
          sources.push(() => source?.close());
        } catch { /* auth hiccup — next refresh retries */ }
        return;
      }

      startPolling();
    };

    // Small stagger so N tasks don't fetch in lockstep.
    (async () => {
      for (const task of tasksToFollowRef.current) {
        if (stopped) break;
        await followTask(task);
      }
    })();

    return () => {
      stopped = true;
      for (const close of sources) close();
    };
    // followKey stands in for tasksToFollow (read via ref) so only a real
    // change in which tasks need following rebuilds the subscriptions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId, sessionGeneration, followKey]);

  return <><MacWorkNotificationsHost /><MobileWorkActivityHost /></>;
}
